import { useMemo, useState } from 'react';
import { Button, Drawer, Segmented, Select, Table, Tabs, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { BookOpen, CalendarClock, CreditCard, Landmark } from 'lucide-react';
import Layout from '../../Layout';
import { lineBalances } from '../Treatment/logic';
import { ktApi, today, useKt } from './ktStore';
import { cardRows, customerCode, staffLabel, TEAM_ROLE, TEAM_ROLES, traceUsage, type CardRow } from './ktCards';
import { dmy, fmtMoney, type CardGate, type Team, type UsageEntry } from './ktLogic';
import { DemoBar, Kpi, PageHeader, Panel, showErrors } from './ktUi';

const STATUS: Record<CardRow['status'], { label: string; color: string }> = {
  active: { label: 'Đang dùng', color: 'green' }, expired: { label: 'Quá hạn', color: 'red' }, completed: { label: 'Đã dùng hết', color: 'default' },
  refunded: { label: 'Đã hoàn tiền', color: 'purple' }, cancelled: { label: 'Đã hủy', color: 'default' },
};
type Filter = 'all' | 'expiring' | 'expired' | 'debt';
const teamText = (t: Team) => TEAM_ROLES.filter(r => t[r]).map(r => `${TEAM_ROLE[r]}: ${staffLabel(t[r]!)}`).join(' · ');

/** Thẻ liệu trình và nhật ký trừ thẻ số riêng (SRS 2.1, 2.2, 2.3, 3.3) */
export default function KeToanCards() {
  const s = useKt();
  const now = today();
  const rows = useMemo(() => cardRows(s, now), [s, now]);
  const [filter, setFilter] = useState<Filter>('all');
  const [detail, setDetail] = useState<string | null>(null);

  const shown = rows.filter(r => (filter === 'all' ? true : filter === 'debt' ? r.debt > 0 : r.warn === filter));
  const detailRow = rows.find(r => r.card.id === detail) || null;
  const expiring = rows.filter(r => r.warn === 'expiring').length;
  const expired = rows.filter(r => r.warn === 'expired').length;
  const debt = rows.filter(r => r.debt > 0);
  const log = useMemo(() => [...s.usageLog].sort((a, b) => b.seq - a.seq), [s.usageLog]);

  const columns: ColumnsType<CardRow> = [
    { title: 'Khách, thẻ', key: 'c', render: (_, r) => (
      <div className="min-w-0">
        <div className="text-gray-900 dark:text-gray-100">{r.card.customerName} <span className="text-[11px] text-gray-400">{customerCode(r.card.customerPhone)}</span></div>
        <div className="text-[11px] text-gray-500">{r.card.packageName}</div>
      </div>
    ) },
    { title: 'Buổi', key: 'b', width: 110, render: (_, r) => (r.card.kind === 'value' ? <span className="text-xs text-gray-500">Thẻ giá trị</span> : <span>{r.used}/{r.total} <span className="text-[11px] text-gray-500">còn {r.left}</span></span>) },
    { title: 'Hạn dùng', key: 'h', width: 150, render: (_, r) => (
      <div>
        <div className="text-xs">{dmy(r.card.expiresAt)}</div>
        {r.warn === 'expired' && <Tag color="red" className="!mr-0">Quá hạn {-r.daysToExpire} ngày</Tag>}
        {r.warn === 'expiring' && <Tag color="orange" className="!mr-0">Còn {r.daysToExpire} ngày</Tag>}
      </div>
    ) },
    { title: 'Giá bán, nợ', key: 'g', width: 150, align: 'right', render: (_, r) => (
      <div>
        <div>{fmtMoney(r.card.price)}</div>
        {r.debt > 0 && <div className="text-[11px] text-rose-600 font-medium">Còn nợ {fmtMoney(r.debt)}</div>}
      </div>
    ) },
    { title: 'Còn trên TK 3387', key: 'n', width: 140, align: 'right', render: (_, r) => fmtMoney(r.remainingNet) },
    { title: 'Bill gốc', key: 'o', width: 110, render: (_, r) => (r.bill ? <span className="text-xs">{r.bill.no}</span> : <Tooltip title="Thẻ chuyển từ MySpa, không có bill trên hệ thống"><span className="text-[11px] text-gray-400">MySpa</span></Tooltip>) },
    { title: 'Trạng thái', key: 's', width: 110, render: (_, r) => <Tag color={STATUS[r.status].color} className="!mr-0">{STATUS[r.status].label}</Tag> },
    { title: '', key: 'a', width: 90, align: 'right', render: (_, r) => (
      <div className="flex justify-end gap-1">
        <Button size="small" onClick={() => setDetail(r.card.id)}>Chi tiết</Button>
      </div>
    ) },
  ];

  const logColumns: ColumnsType<UsageEntry> = [
    { title: 'Số nhật ký', dataIndex: 'no', width: 110, render: (no: string, e) => <span className={e.cancelled ? 'line-through text-gray-400' : 'font-medium'}>{no}</span> },
    { title: 'Ngày', key: 'd', width: 100, render: (_, e) => dmy(e.date) },
    { title: 'Khách, dịch vụ', key: 'c', render: (_, e) => {
      const t = traceUsage(s, e);
      return (
        <div className="min-w-0">
          <div className="text-gray-900 dark:text-gray-100">{t?.card.customerName}</div>
          <div className="text-[11px] text-gray-500">{e.items.map(i => `${i.name} ×${i.qty}`).join(', ')}</div>
          {e.cancelled && <div className="text-[11px] text-rose-600">Đã hủy: {e.cancelled}</div>}
          {e.debtAtUse && !e.cancelled && <Tag color="red" className="!mr-0 !mt-0.5">Làm khi thẻ còn nợ{e.overrideBy ? `, ${e.overrideBy} duyệt vượt` : ''}</Tag>}
        </div>
      );
    } },
    { title: 'Giá trị chưa VAT', key: 'v', width: 140, align: 'right', render: (_, e) => {
      const t = traceUsage(s, e);
      return <Tooltip title={t ? `Gồm VAT ${fmtMoney(t.gross)}` : ''}>{fmtMoney(t?.net || 0)}</Tooltip>;
    } },
    { title: 'Truy về', key: 'o', width: 150, render: (_, e) => <span className="text-xs text-gray-600 dark:text-gray-300">{traceUsage(s, e)?.origin}</span> },
    { title: 'Ekip, máy', key: 't', render: (_, e) => (
      <div className="text-[11px] text-gray-600 dark:text-gray-300">
        {teamText(e.team) || <span className="text-rose-600">Chưa có ekip</span>}
        {e.machine && <div className="text-gray-400">{e.machine}</div>}
      </div>
    ) },
    { title: 'Sổ', key: 'l', width: 80, render: (_, e) => (s.locks[e.date] ? <Tag className="!mr-0">Đã khóa</Tag> : <Tag color="gold" className="!mr-0">Mở</Tag>) },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Thẻ liệu trình và nhật ký trừ thẻ" desc="Từng buổi, hạn thẻ, phần còn trên TK 3387. Trừ thẻ có số riêng, không chen vào số bill thu tiền"
          extra={s.tier === 'chief' ? (
            <Select<CardGate> className="w-64" value={s.cardGate} aria-label="Chốt chặn công nợ" onChange={v => { const r = ktApi.setCardGate(v); if (!r.ok) showErrors(r.errors); }}
              options={[{ value: 'full', label: 'Chốt chặn: trả đủ mới được làm' }, { value: 'paid', label: 'Chốt chặn: dùng trong số đã đóng' }]} />
          ) : undefined} />
        <DemoBar />

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Kpi icon={<CreditCard size={20} />} tone="bg-sky-50 text-sky-600" label="Thẻ đang dùng" value={rows.filter(r => r.status === 'active').length} hint={`Tổng ${rows.length} thẻ`} />
          <Kpi icon={<Landmark size={20} />} tone="bg-emerald-50 text-emerald-600" label="Còn trên TK 3387 (chưa VAT)" value={fmtMoney(rows.reduce((t, r) => t + r.remainingNet, 0))} hint="Kết chuyển sang 511 khi làm từng buổi" />
          <Kpi icon={<CalendarClock size={20} />} tone="bg-amber-50 text-amber-600" label="Sắp hết hạn, quá hạn" value={`${expiring} / ${expired}`} hint="Cảnh báo trước 30 ngày" />
          <Kpi icon={<BookOpen size={20} />} tone="bg-rose-50 text-rose-600" label="Thẻ còn nợ" value={debt.length} hint={`${fmtMoney(debt.reduce((t, r) => t + r.debt, 0))}, đang khóa trừ buổi`} />
        </div>

        <Panel title="Sổ thẻ">
          <Tabs className="px-4" items={[
            { key: 'cards', label: `Thẻ (${rows.length})`, children: (
              <div className="pb-4 space-y-3">
                <Segmented<Filter> value={filter} onChange={setFilter} options={[
                  { value: 'all', label: 'Tất cả' }, { value: 'expiring', label: `Sắp hết hạn (${expiring})` }, { value: 'expired', label: `Quá hạn (${expired})` }, { value: 'debt', label: `Còn nợ (${debt.length})` },
                ]} />
                <Table rowKey={r => r.card.id} size="middle" columns={columns} dataSource={shown} pagination={{ pageSize: 15, hideOnSinglePage: true }} scroll={{ x: 1100 }} locale={{ emptyText: 'Không có thẻ nào' }} />
              </div>
            ) },
            { key: 'log', label: `Nhật ký trừ thẻ (${s.usageLog.length})`, children: (
              <div className="pb-4 space-y-3">
                <p className="text-xs text-gray-500 m-0">Chuỗi số TLT riêng, chạy liên tục. Lượt hủy giữ nguyên số. Lượt trừ bên Liệu trình và lượt quầy ghi ở màn Ca thực hiện tự vào đây; ngày đã khóa thì không chèn lùi.</p>
                <Table rowKey="id" size="middle" columns={logColumns} dataSource={log} pagination={{ pageSize: 20, hideOnSinglePage: true }} scroll={{ x: 1150 }} locale={{ emptyText: 'Chưa có lượt trừ thẻ' }} />
              </div>
            ) },
          ]} />
        </Panel>
      </div>

      <Drawer open={!!detailRow} onClose={() => setDetail(null)} title={detailRow ? `${detailRow.card.packageName} · ${detailRow.card.customerName}` : ''} width={640}>
        {detailRow && <CardDetail row={detailRow} />}
      </Drawer>
    </Layout>
  );
}

function CardDetail({ row }: { row: CardRow }) {
  const s = useKt();
  const c = row.card;
  const lines = lineBalances(c);
  const usages = s.usageLog.filter(e => e.cardId === c.id).sort((a, b) => a.seq - b.seq);
  return (
    <div className="space-y-5 text-sm">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="text-gray-500">Mã KH</div><div>{customerCode(c.customerPhone)}</div>
        <div className="text-gray-500">Bill gốc</div><div>{row.bill ? `${row.bill.no}, ngày ${dmy(row.bill.date)}` : 'Chuyển từ MySpa, không có bill trên hệ thống'}</div>
        <div className="text-gray-500">Giá niêm yết, giá bán</div><div>{fmtMoney(c.listPrice)} → {fmtMoney(c.price)}</div>
        <div className="text-gray-500">Hạn dùng</div><div>{dmy(c.expiresAt)}</div>
        <div className="text-gray-500">Đã thu, còn nợ</div><div>{fmtMoney(c.payments.reduce((t, p) => t + p.amount, 0))}, {fmtMoney(row.debt)}</div>
      </div>

      {row.bill?.lines.some(l => l.list !== undefined) && (
        <div>
          <div className="font-medium mb-1">Bóc tách chiết khấu về từng dịch vụ</div>
          <Table size="small" pagination={false} rowKey="id" dataSource={row.bill.lines} columns={[
            { title: 'Dịch vụ', dataIndex: 'name' },
            { title: 'Niêm yết', key: 'l', align: 'right', render: (_, l) => fmtMoney(l.list ?? l.gross) },
            { title: 'CK dòng', key: 'd', align: 'right', render: (_, l) => fmtMoney(l.lineDiscount || 0) },
            { title: 'CK tổng đơn', key: 'o', align: 'right', render: (_, l) => fmtMoney(l.orderDiscount || 0) },
            { title: 'Net', key: 'n', align: 'right', render: (_, l) => <b>{fmtMoney(l.gross)}</b> },
          ]} />
        </div>
      )}

      <div>
        <div className="font-medium mb-1">Từng dịch vụ trong thẻ</div>
        <Table size="small" pagination={false} rowKey="serviceId" dataSource={lines} locale={{ emptyText: 'Thẻ giá trị, trừ theo giá dịch vụ' }} columns={[
          { title: 'Dịch vụ', key: 'n', render: (_, l) => <span>{l.name}{l.role === 'bonus' && <Tag className="!ml-1">Tặng</Tag>}</span> },
          { title: 'Đã làm', key: 'u', align: 'right', render: (_, l) => `${l.used}/${l.total + l.bonus}` },
          { title: 'Còn', dataIndex: 'available', align: 'right' },
          { title: 'Giá trị 1 buổi', key: 'v', align: 'right', render: (_, l) => fmtMoney(l.unitValue) },
        ]} />
      </div>

      <div>
        <div className="font-medium mb-1">Các lượt trừ ({usages.length})</div>
        {usages.length === 0 && <div className="text-xs text-gray-500">Chưa làm buổi nào</div>}
        <div className="space-y-2">
          {usages.map(e => {
            const t = traceUsage(s, e);
            return (
              <div key={e.id} className="text-xs border-b border-gray-100 dark:border-gray-800 pb-2">
                <div className="flex justify-between gap-2"><b className={e.cancelled ? 'line-through text-gray-400' : ''}>{e.no} · {dmy(e.date)}</b><span>Chưa VAT {fmtMoney(t?.net || 0)}</span></div>
                <div className="text-gray-500">{e.items.map(i => `${i.name} ×${i.qty}`).join(', ')}</div>
                <div className="text-gray-500">{teamText(e.team)}{e.machine ? ` · ${e.machine}` : ''}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
